import React, { useEffect, useState } from 'react';
import apis from '@src/apis';
import {
  ConfirmDialog,
  CustomTable,
  PageFrame,
  ProcessHandler,
} from '@src/components';
import { Button, Divider, IconButton, TextField, Tooltip } from '@mui/material';
import { debounce } from '@src/utils';
import { Add, Delete, Edit } from '@mui/icons-material';
import actions from '@src/redux/actions';
import { useDispatch, useSelector } from 'react-redux';
import { PERMISSIONS, ROLE_VALUE } from '@src/constants';
import AccessControl from '@src/components/PermissionGate/AccessControl';
import SelectPermission from './components/SelectPermission/index';

import { StyledPermissions } from './index.style';
import CreateAccountDialog from './components/CreateAccountDialog';

const Accounts = () => {
  const dispatch = useDispatch();
  const { account: currentAccount } = useSelector((state) => state.auth);

  const [loadingSearch, setLoadingSearch] = useState(false);
  const [searchValue, setSearchValue] = useState('');
  const [accounts, setAccounts] = useState([]);
  const [paging, setPaging] = useState({ total: 0, page: 1 });
  const [isOpenDialog, setIsOpenDialog] = useState(false);
  const [updatingAccount, setUpdatingAccount] = useState(null);
  const [deletingAccountId, setDeletingAccountId] = useState(null);

  const isSelectPermissionDisabled = !(
    currentAccount?.roles?.includes(ROLE_VALUE.SUPER_ADMIN) ||
    currentAccount?.permissions?.includes(PERMISSIONS.MANAGE_ACCOUNT)
  );

  const inputSearchChange = (event) => {
    setSearchValue(event.target.value);
  };

  const handleOpenDialog = () => {
    setIsOpenDialog(true);
  };

  const handleCloseDeleteDialog = () => {
    setDeletingAccountId(null);
  };

  const handleCloseDialog = () => {
    setIsOpenDialog(false);
    if (updatingAccount) {
      setUpdatingAccount(null);
    }
  };

  const handleChangePage = (page) => {
    setPaging((prevState) => ({ ...prevState, page }));
  };

  const handleCreateNewAccountDialog = (newAccount) => {
    setIsOpenDialog(false);
    if (paging.page === 1) {
      setAccounts((prevAccount) => [newAccount, ...prevAccount]);
      setPaging((prev) => ({ ...paging, total: prev.total + 1 }));
    }
  };

  const handleUpdateAccount = (updatedAccount) => {
    setUpdatingAccount(null);
    setAccounts((prevAccount) =>
      prevAccount.map((account) => {
        if (account?.id === updatedAccount?.id) {
          return updatedAccount;
        }
        return account;
      }),
    );
  };

  const handleDeleteAccount = async () => {
    const res = await apis.account.deleteAccount(deletingAccountId);

    if (!res?.status) {
      dispatch(
        actions.noti.push({
          code: res?.code || 0,
          severity: 'error',
          message: res?.message,
        }),
      );
      return;
    }
    dispatch(
      actions.noti.push({
        message: 'Xóa tài khoản thành công',
        severity: 'success',
      }),
    );

    setAccounts((prev) =>
      prev.filter((account) => account.id !== deletingAccountId),
    );
    setDeletingAccountId(null);
  };

  const fetchAccounts = async () => {
    setLoadingSearch(true);
    const res = await apis.account.getAccounts({
      searchFields: 'username',
      search: searchValue,
      page: paging.page,
    });
    setLoadingSearch(false);
    if (!res?.status) return;

    setAccounts(res.result.accounts);
    setPaging({ ...paging, total: res.result.total });
  };

  const columns = [
    {
      field: 'username',
      align: 'left',
      id: 'name',
      title: 'Tên tài khoản',
      render: (account) => account.username,
    },
    {
      field: 'facility-name',
      align: 'center',
      id: 'facility-name',
      title: 'Tên cơ sở làm việc',
      render: (account) => account.facility.name,
    },
    {
      field: 'role',
      align: 'center',
      id: 'role',
      title: 'Chức vụ',
      render: (account) => {
        const isAdmin = account.roles?.includes(ROLE_VALUE.SUPER_ADMIN);
        return isAdmin ? 'Quản trị viên' : 'Nhân viên';
      },
    },
    {
      field: 'select-permission',
      align: 'center',
      id: 'select-permission',
      title: 'Quyền truy cập',
      render: (account) => {
        if (account.roles?.includes(ROLE_VALUE.SUPER_ADMIN)) {
          return null;
        }
        return (
          <SelectPermission
            account={account}
            accountId={account.id}
            currentPermissions={account.permissions}
            fetchAccounts={fetchAccounts}
            isDisabled={isSelectPermissionDisabled}
            customStyle={{
              width: '500px',
            }}
          />
        );
      },
    },
    {
      id: 'actions',
      align: 'center',
      title: 'Hành động',
      requiredPermission: PERMISSIONS.MANAGE_ACCOUNT,
      render: (account) => (
        <>
          <Tooltip title="Xóa">
            <IconButton
              // disabled={account.roles?.includes(ROLE_VALUE.SUPER_ADMIN)}
              onClick={() => {
                setDeletingAccountId(account.id);
              }}
            >
              <Delete />
            </IconButton>
          </Tooltip>
          <Tooltip title="Chỉnh sửa">
            <IconButton
              // disabled={account.roles?.includes(ROLE_VALUE.SUPER_ADMIN)}
              onClick={() => {
                setUpdatingAccount(account);
              }}
            >
              <Edit />
            </IconButton>
          </Tooltip>
        </>
      ),
    },
  ];

  useEffect(() => {
    debounce(fetchAccounts, 500)(searchValue);
  }, [paging.page, searchValue]);

  return (
    <PageFrame title="Cấu hình quyền truy cập">
      <StyledPermissions>
        <Divider />
        <div className="search">
          <TextField
            className="input-search"
            size="small"
            placeholder="Tìm kiếm nhân viên"
            onChange={inputSearchChange}
            InputProps={{
              endAdornment: loadingSearch ? (
                <ProcessHandler loading size={20} />
              ) : null,
            }}
          />
          <div className="create-account">
            <AccessControl permissions={[PERMISSIONS.MANAGE_ACCOUNT]}>
              <Button
                variant="outlined"
                disableElevation
                endIcon={<Add />}
                size="small"
                onClick={handleOpenDialog}
              >
                Thêm mới tải khoản
              </Button>
            </AccessControl>
            <CreateAccountDialog
              onClose={handleCloseDialog}
              open={isOpenDialog}
              onCreateNewAccount={handleCreateNewAccountDialog}
            />
            <CreateAccountDialog
              onClose={handleCloseDialog}
              account={updatingAccount}
              open={Boolean(updatingAccount)}
              onUpdateProduct={handleUpdateAccount}
            />
          </div>
        </div>
        <CustomTable
          columns={columns}
          data={accounts}
          total={paging.total}
          page={paging.page}
          onChangePage={handleChangePage}
        />
        <ConfirmDialog
          open={Boolean(deletingAccountId)}
          title="Tài khoản"
          description="Bạn có muốn xóa tài khoản này?"
          titleOk="Đồng ý"
          titleCancel="Hủy"
          onCancel={handleCloseDeleteDialog}
          onClose={handleCloseDeleteDialog}
          onOk={handleDeleteAccount}
        />
      </StyledPermissions>
    </PageFrame>
  );
};

export default Accounts;
